/**
 * IonLanguageLine — the multilingual "Ion speaks your language" flourish.
 *
 * Extracted from ChatEmptyState.jsx (where it lived as a private
 * `LanguageTypewriter`) so the FULL-WIDTH Ion-Home screen can show it too.
 * BA asked for it on the landing screen, and the landing screen is where a
 * first-time user actually starts — a fact about Ion that only the narrow
 * docked panel advertised was on the wrong surface.
 *
 * Extracted rather than copied: two implementations of the same typing
 * animation would drift, and the reduced-motion handling is exactly the kind
 * of detail that gets fixed in one copy and not the other.
 *
 * Markup and class names are unchanged (`.ion-empty__languages`,
 * `.ion-empty__lang*` in ion.css) so the docked view renders byte-identically
 * to before. The classes are NOT geometry-scoped, so they apply at full width
 * as well — verified against the selectors, not assumed.
 */

import { useEffect, useState } from 'preact/hooks'
import { t } from '../../i18n/index.js'

// The sentences ARE the translations, so they are literal strings rather than
// i18n keys. Order: de · fr · es · zh · ko · ja.
export const LANGUAGE_LINES = [
  'Ich spreche deine Sprache.',
  'Je parle ta langue.',
  'Hablo tu idioma.',
  '我说你的语言。',
  '저는 당신의 말을 할 줄 알아요.',
  '私はあなたの言葉を話せます。',
]

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Types each sentence, holds, deletes, and moves to the next — cycling
 * forever. One clean line instead of a wrapped block. The full list is always
 * present (visually hidden) for screen readers and tests; the animated line is
 * aria-hidden. Respects prefers-reduced-motion by showing the first sentence
 * statically with no timers.
 *
 * @param {object} props
 * @param {string[]} [props.lines] — override the sentence list (tests)
 */
export function LanguageTypewriter({ lines = LANGUAGE_LINES }) {
  const [text, setText] = useState(prefersReducedMotion() ? lines[0] : '')

  useEffect(() => {
    if (prefersReducedMotion()) return
    let timer = null
    let active = true
    const s = { line: 0, char: 0, deleting: false }

    const tick = () => {
      if (!active) return
      const full = lines[s.line]
      if (!s.deleting) {
        s.char += 1
        setText(full.slice(0, s.char))
        if (s.char >= full.length) {
          s.deleting = true
          timer = setTimeout(tick, 1500); // hold on the full sentence
          return
        }
        timer = setTimeout(tick, 60); // type speed
      } else {
        s.char -= 1
        setText(full.slice(0, Math.max(0, s.char)))
        if (s.char <= 0) {
          s.deleting = false
          s.line = (s.line + 1) % lines.length
          timer = setTimeout(tick, 320); // pause before the next language
          return
        }
        timer = setTimeout(tick, 28); // delete speed (faster)
      }
    }

    timer = setTimeout(tick, 450)
    return () => {
      active = false
      if (timer) clearTimeout(timer)
    }
  }, [lines])

  return (
    <span class="ion-empty__lang">
      <span class="ion-empty__lang-typed" aria-hidden="true">
        {text}
        <span class="ion-empty__lang-caret" aria-hidden="true" />
      </span>
      <span class="ion-empty__lang-sr">{lines.join(' · ')}</span>
    </span>
  )
}

/**
 * The labelled block both empty states render: the uppercase "Languages"
 * caption plus the typing line. Kept as one component so the two surfaces
 * cannot drift on the label or the aria-label.
 */
export function IonLanguageLine() {
  return (
    <div class="ion-empty__languages" aria-label="Ion speaks your language">
      <span class="ion-empty__languages-label">{t('ion.welcome.languages')}</span>
      <LanguageTypewriter />
    </div>
  )
}
